import React from 'react';
import { Eye, AlertTriangle, CheckCircle } from 'lucide-react';

interface AttentionMonitorProps {
  attentionScore: number;
  isActive: boolean;
}

export default function AttentionMonitor({ attentionScore, isActive }: AttentionMonitorProps) {
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBackground = (score: number) => {
    if (score >= 80) return 'bg-green-600';
    if (score >= 60) return 'bg-yellow-600';
    return 'bg-red-600';
  };

  return (
    <div className="p-4 border-b border-gray-700">
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-2 flex items-center">
          <Eye className="w-5 h-5 mr-2" />
          Attention Monitor
        </h3>
        
        <div className="space-y-3">
          {/* Attention Score */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-400">Attention Score</span>
              <span className={`text-lg font-bold ${getScoreColor(attentionScore)}`}>
                {attentionScore}%
              </span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${getScoreBackground(attentionScore)}`}
                style={{ width: `${attentionScore}%` }}
              />
            </div>
          </div>

          {/* Status Indicator */}
          <div className="flex items-center space-x-2">
            {isActive ? (
              <>
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm text-green-400">Monitoring Active</span>
              </>
            ) : (
              <>
                <div className="w-3 h-3 bg-gray-500 rounded-full" />
                <span className="text-sm text-gray-400">Monitoring Paused</span>
              </>
            )}
          </div>

          {/* Performance Indicators */}
          <div className="grid grid-cols-2 gap-2 mt-4">
            <div className="bg-gray-700 rounded-lg p-3 text-center">
              <CheckCircle className="w-4 h-4 text-green-400 mx-auto mb-1" />
              <div className="text-xs text-gray-400">Correct</div>
              <div className="text-sm font-semibold">42</div>
            </div>
            <div className="bg-gray-700 rounded-lg p-3 text-center">
              <AlertTriangle className="w-4 h-4 text-red-400 mx-auto mb-1" />
              <div className="text-xs text-gray-400">Missed</div>
              <div className="text-sm font-semibold">3</div>
            </div>
          </div>

          {/* Attention Tips */}
          <div className="mt-4 p-3 bg-blue-900 bg-opacity-50 rounded-lg">
            <h4 className="text-sm font-medium text-blue-300 mb-2">Attention Tips</h4>
            <ul className="text-xs text-blue-200 space-y-1">
              <li>• Maintain eye contact with screen</li>
              <li>• Respond promptly to questions</li>
              <li>• Take notes during key points</li>
              <li>• Minimize distractions</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}